/**
 * yt-practitioner-plan.js — controller for sections/yt-practitioner-plan.liquid
 * (partner programme Phase 3, docs/12-partners/02 §3.3).
 *
 * A client opens the link their practitioner sent. The link carries the
 * plan token in the URL FRAGMENT (#t=<43-char base64url>). We stash it in
 * sessionStorage, strip it from the address bar, then:
 *
 *   GET  /apps/yutrients/partner-plan/<token>
 *        → { state:'active', expires_at, practitioner:{display_name,
 *            avatar_url?}, client:{first_name}, message,
 *            consent:{version, text, already_granted},
 *            stacks:[{stack_id, formula_id, name, suggested_mode, base|null,
 *                     ingredients, nutrition, prices:{sub, one_time,
 *                     monthly_90d|null}, plans:['sub','onetime'(,'90d')],
 *                     safety? }] }
 *        404 unknown token · 410 {state:'expired'|'revoked'|'inactive'}
 *   POST /apps/yutrients/partner-plan/<token>/cart
 *        { selections:[{stack_id, mode}], consent:{accepted:true, version} }
 *        → { lines:[{id, quantity, selling_plan?, properties}],
 *            groups:[{stack_id, group_key, mode}], discount, discount_url }
 *        422 consent_required · 422 when a 90-day plan isn't available
 *
 * Then ONE /cart/add.js call with the lines REVERSED (Shopify prepends, so
 * the basket reads in server order — same rule as yt-bundle-pdp.js), then
 * /cart/update.js sets the cart attribute `_yt_plan` so the site-wide
 * referral script leaves this basket alone, then STRAIGHT TO CHECKOUT
 * (UAT 23 Sep 2026: "switch Add to Basket to Checkout, which goes direct
 * to Checkout") — the side cart is never opened from this page.
 *
 * UAT 23 Sep 2026, purchase options: the 90-day plan is never offered
 * (whatever the payload lists), the practitioner's suggested_mode is the
 * RECOMMENDED option — shown first, pre-selected, wearing their name —
 * and the other way to buy sits under it, de-emphasised but a tap away.
 * Sharing consent is REQUIRED to use the plan and is presented as such:
 * the button refuses until the box is ticked (and the server answers 422
 * consent_required regardless), and the line under the button says why
 * before anyone presses it (UAT 9 Oct 2026).
 *
 * THE DISCOUNT APPLIES ITSELF (founder 17 Sep 2026: "the discount code
 * should be auto applied in checkout"). The client never types a code.
 * The GET says what the practitioner's clients get — we show it honestly
 * in the price area, reading the number from the payload — and the POST
 * answers with `discount_url` (`/discount/<CODE>?redirect=/checkout`).
 * From that moment, every route out of this page that ends at checkout
 * goes through that URL instead: straight there when the theme has no
 * side cart, and otherwise on the click that leaves the drawer for
 * checkout, so someone who is only looking still gets their basket. We
 * never build that URL ourselves — the server hands it over, and we
 * refuse anything that is not a plain same-site /discount/… path.
 *
 * Hygiene: the token only ever travels in the two proxied fetch paths the
 * contract defines — never in the address bar, links, storage other than
 * sessionStorage, or any tracking call. No analytics fire from this file.
 */
(function () {
  'use strict';

  var PROXY = '/apps/yutrients';
  var TOKEN_KEY = 'yt_plan_token';
  var TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
  var PLAN_ATTR = '_yt_plan';
  var CONSENT_VERSION_FALLBACK = 'prt-consent-v1';

  function money(n) {
    return '£' + (Math.round(Number(n) * 100) / 100).toFixed(2);
  }
  function round2(n) { return Math.round(Number(n) * 100) / 100; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function firstName(full) {
    var s = String(full || '').trim();
    return s ? s.split(/\s+/)[0] : '';
  }
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (_e) { return d.toDateString(); }
  }
  async function safeJson(res) {
    try { return await res.json(); } catch (_e) { return null; }
  }

  // ── The discount (founder 17 Sep 2026) ──────────────────────────────
  // Where the customer goes to have the code applied, once the server has
  // told us. Null until then, and null for a partner whose split leaves
  // the client nothing — the page is then exactly what it was before.
  var pendingDiscountUrl = null;
  var leaving = false;

  /**
   * The server's `discount_url`, or null. It must be a same-site path of
   * the shape `/discount/<code>` with an optional query and nothing else:
   * a protocol-relative `//elsewhere`, a full URL, a second path segment
   * or any whitespace is refused rather than navigated to.
   */
  function safeDiscountUrl(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s || s.charAt(0) !== '/' || s.charAt(1) === '/' || s.charAt(1) === '\\') return null;
    var q = s.indexOf('?');
    var path = q < 0 ? s : s.slice(0, q);
    if (!/^\/discount\/[^/?#\s]+$/.test(path)) return null;
    if (q >= 0 && /[\s#]/.test(s.slice(q))) return null;
    return s;
  }

  /** A percentage as a person would write it: 10, 12.5 — never "10.00". */
  function pct(n) {
    var v = Number(n);
    if (!isFinite(v) || v <= 0) return null;
    return String(Math.round(v * 100) / 100);
  }

  /** The saving, in the price area, in the payload's own number. */
  function savingHtml(discount) {
    var p = discount && pct(discount.percent);
    if (!p) return '';
    // Addressed to the client reading the page, not about them: they are
    // the one paying, and they do not have to type anything.
    return '<span class="ytp-total__save" data-ytp-saving>Your practitioner’s ' +
      esc(p) + '% discount is applied at checkout. There is no code to enter.</span>';
  }

  /** One navigation, and never a second one on top of it. */
  function leaveFor(url) {
    if (leaving) return;
    leaving = true;
    window.location.assign(url);
  }

  // ── Token ───────────────────────────────────────────────────────────
  // The section also runs a tiny parse-time stash-and-strip so the
  // fragment is gone before most of the page's other scripts run; this is
  // the belt to that brace.
  function readToken() {
    var t = null;
    var m = /(?:^#|[#&])t=([A-Za-z0-9_-]+)(?:&|$)/.exec(location.hash || '');
    if (m) {
      t = m[1];
      try { sessionStorage.setItem(TOKEN_KEY, t); } catch (_e) {}
      try { history.replaceState(null, '', location.pathname + location.search); } catch (_e) {}
    }
    if (!t) {
      try { t = sessionStorage.getItem(TOKEN_KEY); } catch (_e) {}
    }
    return t && TOKEN_RE.test(t) ? t : null;
  }

  async function tokenHashPrefix(token) {
    try {
      if (window.crypto && crypto.subtle && window.TextEncoder) {
        var buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
        return Array.prototype.map.call(new Uint8Array(buf), function (b) {
          return ('0' + b.toString(16)).slice(-2);
        }).join('').slice(0, 16);
      }
    } catch (_e) {}
    return 'plan';
  }

  // ── Dose display (same rules as yt-bundle-pdp.js) ───────────────────
  function doseParts(ing) {
    if (ing.dose_num != null && ing.dose_unit != null) {
      return { num: String(ing.dose_num), unit: String(ing.dose_unit).trim() };
    }
    var mg = Number(ing.amount_mg);
    if (!isFinite(mg)) return { num: '', unit: '' };
    var unit = String(ing.unit || '').toLowerCase();
    if (unit === 'bn_cfu') return { num: String(round2(mg)), unit: 'bn CFU' };
    var isMcg = unit === 'mcg' || (!ing.unit && mg > 0 && mg < 1);
    if (isMcg) return { num: String(Math.round(mg * 1000)), unit: 'µg' };
    var v = round2(mg);
    return { num: Number.isInteger(v) ? String(v) : v.toFixed(2), unit: 'mg' };
  }

  // ── Safety (stacks[].safety, shape not fixed yet — accept the likely
  //    forms, omit the line when nothing says "above the customer max") ──
  function aboveCustomerMax(stack) {
    var s = stack && stack.safety;
    if (!s) return false;
    if (s === true) return true;
    if (Array.isArray(s)) return s.length > 0;
    if (typeof s === 'object') {
      // An EMPTY above_customer_max list is the common case and means
      // nothing is above the max (an empty array is truthy — the line
      // used to show on every blend).
      var listed = function (v) { return Array.isArray(v) ? v.length > 0 : v === true; };
      return !!(listed(s.above_customer_max) || listed(s.above_max) || s.flagged === true ||
        s.status === 'flagged' || listed(s.flags));
    }
    return false;
  }

  // ── Consent fallback wording (docs/12-partners/legal/client-consent-v1.md)
  function consentLabelFallback(name) {
    return 'I agree that ' + name + ' can see the blends in this plan and how my orders and subscription for them are going. I can stop this at any time.';
  }
  function consentPanelFallback(name, first) {
    return [
      { heading: 'What ' + first + ' will see', items: [
        'The blends in this plan: the base, the ingredients and the daily amounts.',
        'Whether you have ordered them, the order date and when they were sent.',
        'For a subscription: whether it is active, how often it renews, the next charge date and the price per charge.',
        'Any change they suggest to one of these subscriptions, and what you decided.',
      ] },
      { heading: 'What they will never see', items: [
        'Your address, phone number or payment details.',
        'Any other blend, order or subscription you have with Yutrients.',
        'Your full email address (we show it partly hidden).',
      ] },
      { heading: 'Why we ask', text:
        'Your blends can say something about your health, so we only share them with your practitioner because you tell us to. Yutrients is the company that holds your account and orders; ' + name + ' keeps their own client records separately and is responsible for those.' },
      { heading: 'How to stop sharing', text:
        'Any time, in three ways: in your Yutrients account under Manage subscription → Sharing; using the Stop sharing link at the bottom of any plan email; or by contacting us. Stopping sharing does not change or cancel your subscription — it only ends what your practitioner can see. If your practitioner has suggested a change that you have not yet decided on, it is cancelled.' },
    ];
  }
  function safetyLine(name) {
    return 'Some amounts are above our standard daily maximum. ' + name + ' has set them for you as your practitioner. If you are pregnant, breastfeeding, taking medication or under medical care, speak to your GP or pharmacist first.';
  }

  // ── Boot ────────────────────────────────────────────────────────────
  var root, panels, cfg;

  function setState(name) {
    root.setAttribute('data-yt-state', name);
    Object.keys(panels).forEach(function (k) { panels[k].hidden = k !== name; });
    if (name !== 'loading' && name !== 'plan') {
      var h = panels[name] && panels[name].querySelector('h1, h2');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (_e) {} }
    }
  }
  function stateFor(s) {
    return s === 'expired' || s === 'revoked' || s === 'inactive' ? s : 'invalid';
  }
  function showRetry(fn) {
    var btn = panels.error.querySelector('[data-ytp-retry]');
    if (btn) { btn.onclick = fn; btn.disabled = false; }
    setState('error');
  }

  async function loadPlan(token) {
    setState('loading');
    var res;
    try {
      res = await fetch(PROXY + '/partner-plan/' + encodeURIComponent(token), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
    } catch (_e) {
      return showRetry(function () { loadPlan(token); });
    }
    if (res.status === 404) return setState('invalid');
    if (res.status === 410) {
      var gone = await safeJson(res);
      return setState(stateFor(gone && gone.state));
    }
    if (!res.ok) return showRetry(function () { loadPlan(token); });
    var plan = await safeJson(res);
    if (!plan || !Array.isArray(plan.stacks) || !plan.stacks.length) {
      return setState(plan && plan.state && plan.state !== 'active' ? stateFor(plan.state) : 'invalid');
    }
    if (plan.state && plan.state !== 'active') return setState(stateFor(plan.state));
    renderPlan(plan, token);
  }

  // ── Render ──────────────────────────────────────────────────────────
  function stackPlans(st) {
    var prices = st.prices || {};
    var plans = Array.isArray(st.plans) && st.plans.length ? st.plans.slice() : ['sub', 'onetime'];
    // The 90-day plan is not offered to clients (UAT 23 Sep 2026).
    return plans.filter(function (p) {
      if (p === 'sub') return prices.sub != null;
      if (p === 'onetime') return prices.one_time != null;
      return false;
    });
  }
  function priceFor(st, mode) {
    var p = st.prices || {};
    return mode === 'onetime' ? Number(p.one_time) : mode === '90d' ? Number(p.monthly_90d) : Number(p.sub);
  }
  function defaultMode(st, plans) {
    if (st.suggested_mode && plans.indexOf(st.suggested_mode) >= 0) return st.suggested_mode;
    if (plans.indexOf('sub') >= 0) return 'sub';
    return plans[0];
  }
  /** The recommended option first, the rest after it. */
  function orderedPlans(st, plans) {
    var rec = defaultMode(st, plans);
    return plans.slice().sort(function (a, b) { return a === rec ? -1 : b === rec ? 1 : 0; });
  }

  function planOptionHtml(st, mode, price, checked, recommended, pracFirst) {
    var title, note, amount, per;
    if (mode === 'sub') {
      title = 'Every month'; amount = money(price); per = 'a month';
      note = 'One tin every 30 days. Pause or change it from your account.';
    } else {
      title = 'One-off tin'; amount = money(price); per = 'once';
      note = 'A single 30-day tin. No subscription.';
    }
    return (
      '<label class="ytp-plan' + (checked ? ' is-on' : '') + (recommended ? ' ytp-plan--rec' : ' ytp-plan--alt') + '">' +
        '<input type="radio" name="ytp-plan-' + esc(st.stack_id) + '" value="' + esc(mode) + '"' + (checked ? ' checked' : '') + ' data-ytp-mode>' +
        '<span class="ytp-plan__dot" aria-hidden="true"></span>' +
        '<span class="ytp-plan__body">' +
          '<span class="ytp-plan__t">' + esc(title) +
            (recommended ? '<span class="ytp-plan__rec">' + esc(pracFirst || 'Your practitioner') + ' recommends</span>' : '') +
          '</span>' +
          '<span class="ytp-plan__n">' + esc(note) + '</span>' +
        '</span>' +
        '<span class="ytp-plan__price"><b>' + esc(amount) + '</b><span>' + esc(per) + '</span></span>' +
      '</label>'
    );
  }
  /** Every option for a stack, recommended first, ready for a fieldset. */
  function planOptionsHtml(m, pracFirst) {
    var st = m.raw;
    var rec = defaultMode(st, m.plans);
    return orderedPlans(st, m.plans).map(function (p) {
      return planOptionHtml(st, p, priceFor(st, p), p === m.mode, p === rec, pracFirst);
    }).join('');
  }

  function rowsHtml(st) {
    var out = '';
    if (st.base) {
      out +=
        '<li class="ytp-row ytp-row--base">' +
          '<span class="ytp-row__img ytp-row__img--base" aria-hidden="true">Base</span>' +
          '<span class="ytp-row__b">' +
            '<span class="ytp-row__n">' + esc(st.base.name) + '</span>' +
            '<span class="ytp-row__w">Base blend · ' + esc(st.base.n_ingredients) + ' ingredients</span>' +
          '</span>' +
          '<span class="ytp-row__d ytp-row__d--muted">' + esc(st.base.n_ingredients) + '<small>ingredients</small></span>' +
        '</li>';
    }
    (st.ingredients || []).forEach(function (ing) {
      var d = doseParts(ing);
      out +=
        '<li class="ytp-row">' +
          '<span class="ytp-row__img" data-yt-ing-slug="' + esc(ing.slug || '') + '" aria-hidden="true"><span class="ytp-row__img-fill"></span></span>' +
          '<span class="ytp-row__b"><span class="ytp-row__n">' + esc(ing.name) + '</span>' +
            (st.base ? '<span class="ytp-row__w">Added to the base</span>' : '') +
          '</span>' +
          '<span class="ytp-row__d">' + esc(d.num) + '<small>' + esc(d.unit) + ' /day</small></span>' +
        '</li>';
    });
    return out;
  }

  function nutritionHtml(st) {
    var rows = Array.isArray(st.nutrition) ? st.nutrition : [];
    if (!rows.length) return '';
    return (
      '<details class="ytp-nutrition">' +
        '<summary><span>Full ingredient list and daily amounts</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></summary>' +
        '<div class="ytp-nutrition__scroll"><table class="ytp-nutrition__t">' +
          '<thead><tr><th scope="col">Nutritional information</th><th scope="col">Per daily dose</th><th scope="col">%NRV</th></tr></thead><tbody>' +
          rows.map(function (r) {
            return '<tr><td>' + esc(r.panel_name) + '</td><td>' + esc(r.dose_display) + '</td><td>' + (r.nrv_pct != null ? esc(r.nrv_pct) + '%' : '†') + '</td></tr>';
          }).join('') +
        '</tbody></table></div>' +
        '<p class="ytp-small">† No nutrient reference value set. Amounts are per daily scoop.</p>' +
      '</details>'
    );
  }

  function consentBlockHtml(plan, pracName, pracFirst, privacyUrl, sharingUrl) {
    var c = plan.consent || {};
    if (c.already_granted) {
      return (
        '<div class="ytp-consent ytp-consent--granted" data-ytp-consent-block>' +
          '<p class="ytp-consent__granted">You already share these with <b>' + esc(pracName) + '</b>. ' +
            '<a href="' + esc(sharingUrl) + '">Change</a></p>' +
        '</div>'
      );
    }
    var label = (c.text && String(c.text).trim()) || consentLabelFallback(pracName);
    var panel = null;
    if (Array.isArray(c.details) && c.details.length) panel = c.details;
    else if (typeof c.details === 'string' && c.details.trim()) panel = [{ text: c.details }];
    else panel = consentPanelFallback(pracName, pracFirst);
    var panelHtml = panel.map(function (blk) {
      var h = blk && blk.heading ? '<h3 class="ytp-consent__h">' + esc(blk.heading) + '</h3>' : '';
      var body = '';
      if (blk && Array.isArray(blk.items)) body = '<ul>' + blk.items.map(function (i) { return '<li>' + esc(i) + '</li>'; }).join('') + '</ul>';
      else if (blk && blk.text) body = '<p>' + esc(blk.text) + '</p>';
      return h + body;
    }).join('');
    return (
      '<div class="ytp-consent" data-ytp-consent-block>' +
        '<p class="ytp-consent__lead"><span class="ytp-consent__req">Required</span> ' +
          esc(pracFirst || 'Your practitioner') + ' needs to see how this plan goes for you. Tick to agree and go to checkout.</p>' +
        '<label class="ytp-check">' +
          '<input type="checkbox" data-ytp-consent aria-describedby="ytp-consent-err" aria-required="true">' +
          '<span class="ytp-check__box" aria-hidden="true"></span>' +
          '<span class="ytp-check__t" data-ytp-consent-text>' + esc(label) + '</span>' +
        '</label>' +
        '<p class="ytp-consent__err" id="ytp-consent-err" data-ytp-consent-err role="alert" hidden>Please tick the box above — the plan can only be used with sharing agreed.</p>' +
        '<details class="ytp-more">' +
          '<summary><span>What ' + esc(pracFirst || 'your practitioner') + ' will and won’t see</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></summary>' +
          '<div class="ytp-more__body">' + panelHtml +
            '<p>Full details are in our <a href="' + esc(privacyUrl) + '">Privacy Policy</a>.</p>' +
          '</div>' +
        '</details>' +
      '</div>'
    );
  }

  function renderPlan(plan, token) {
    var pracName = (plan.practitioner && plan.practitioner.display_name) || 'Your practitioner';
    var pracFirst = firstName(pracName) || 'your practitioner';
    var clientFirst = firstName(plan.client && plan.client.first_name);
    var avatar = plan.practitioner && plan.practitioner.avatar_url;
    var expires = fmtDate(plan.expires_at);
    var n = plan.stacks.length;

    var model = {
      token: token,
      pracName: pracName,
      pracFirst: pracFirst,
      version: (plan.consent && plan.consent.version) || CONSENT_VERSION_FALLBACK,
      granted: !!(plan.consent && plan.consent.already_granted),
      stacks: plan.stacks.map(function (st) {
        var plans = stackPlans(st);
        return { id: st.stack_id, raw: st, plans: plans, mode: defaultMode(st, plans), include: true };
      }),
    };

    var head =
      '<header class="ytp-head">' +
        '<div class="ytp-prac">' +
          (avatar
            ? '<img class="ytp-prac__img" src="' + esc(avatar) + '" alt="" width="56" height="56" loading="eager" referrerpolicy="no-referrer">'
            : '<span class="ytp-prac__img ytp-prac__img--initial" aria-hidden="true">' + esc((pracFirst || 'Y').charAt(0).toUpperCase()) + '</span>') +
          '<span class="ytp-prac__b"><span class="ytp-eyebrow">Prepared for you by</span><span class="ytp-prac__n">' + esc(pracName) + '</span></span>' +
        '</div>' +
        '<h1 class="ytp-h1">' + (clientFirst ? 'Hi ' + esc(clientFirst) + ', ' : '') + 'here’s the plan ' + esc(pracFirst) + ' put together for you.</h1>' +
        (plan.message && String(plan.message).trim()
          ? '<blockquote class="ytp-msg"><p data-ytp-message></p><cite>' + esc(pracName) + '</cite></blockquote>'
          : '') +
        '<p class="ytp-small">' + (n === 1 ? 'One blend' : n + ' blends') + ', each weighed to your daily amounts and made to order.' +
          (expires ? ' This link is valid until ' + esc(expires) + '.' : '') + '</p>' +
      '</header>';

    var cards = model.stacks.map(function (m, i) {
      var st = m.raw;
      return (
        '<article class="ytp-card" data-ytp-stack="' + esc(m.id) + '">' +
          '<div class="ytp-card__head">' +
            '<div class="ytp-card__title">' +
              (n > 1 ? '<p class="ytp-eyebrow">Blend ' + (i + 1) + ' of ' + n + '</p>' : '<p class="ytp-eyebrow">Your blend</p>') +
              '<h2 class="ytp-h2">' + esc(st.name) + '</h2>' +
            '</div>' +
            '<label class="ytp-switch">' +
              '<input type="checkbox" checked data-ytp-include>' +
              '<span class="ytp-switch__track" aria-hidden="true"></span>' +
              '<span class="ytp-switch__t">Include this blend</span>' +
            '</label>' +
          '</div>' +
          '<ul class="ytp-rows">' + rowsHtml(st) + '</ul>' +
          nutritionHtml(st) +
          (aboveCustomerMax(st) ? '<p class="ytp-safety">' + esc(safetyLine(pracName)) + '</p>' : '') +
          '<fieldset class="ytp-plans">' +
            '<legend class="ytp-eyebrow">How would you like it?</legend>' +
            planOptionsHtml(m, pracFirst) +
          '</fieldset>' +
          '<p class="ytp-card__line"><span>This blend</span><b data-ytp-line></b></p>' +
        '</article>'
      );
    }).join('');

    var aside =
      '<aside class="ytp-summary" aria-label="Your total and consent">' +
        '<div class="ytp-total">' +
          '<span class="ytp-eyebrow">Your total</span>' +
          '<span class="ytp-total__n" data-ytp-total aria-live="polite"></span>' +
          '<span class="ytp-total__d" data-ytp-total-detail></span>' +
          // The saving is stated before the total changes, not after: the
          // prices above are the list prices, and the code comes off at
          // checkout without anyone typing it.
          savingHtml(plan.discount) +
        '</div>' +
        consentBlockHtml(plan, pracName, pracFirst, cfg.privacyUrl, cfg.sharingUrl) +
        '<div class="ytp-error" data-ytp-error role="alert" hidden></div>' +
        '<button type="button" class="ytp-btn" data-ytp-add>' + esc(cfg.addLabel) + '</button>' +
        '<p class="ytp-small ytp-summary__note" data-ytp-note>You can review everything at checkout before you pay.</p>' +
        '<ul class="ytp-trust" aria-label="Good to know">' +
          '<li>Made to order in our own UK lab</li>' +
          '<li>30-day money-back guarantee</li>' +
          '<li>Pause or cancel a subscription any time</li>' +
        '</ul>' +
      '</aside>';

    panels.plan.innerHTML =
      '<div class="ytp-grid">' +
        '<div class="ytp-main">' + head + '<div class="ytp-stacks">' + cards + '</div></div>' +
        aside +
      '</div>';

    // Message is plain text — set via textContent so nothing in it renders.
    var msgEl = panels.plan.querySelector('[data-ytp-message]');
    if (msgEl) msgEl.textContent = String(plan.message).trim();

    hydrateThumbs(panels.plan);
    bind(model);
    updateTotals(model);
    setState('plan');
  }

  // Ingredient photos — the baked shop metafield map first (same as the
  // PDPs), then the ingredient's own product JSON (first-party) as fallback.
  function hydrateThumbs(scope) {
    var baked = window.__ytIngredientImages || {};
    scope.querySelectorAll('[data-yt-ing-slug]').forEach(function (el) {
      var slug = el.getAttribute('data-yt-ing-slug');
      if (!slug) return;
      var fill = el.querySelector('.ytp-row__img-fill');
      if (!fill) return;
      if (baked[slug]) { fill.style.backgroundImage = 'url(' + baked[slug] + ')'; el.classList.add('is-loaded'); return; }
      fetch('/products/' + encodeURIComponent(slug) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          var img = prod && (prod.featured_image || (prod.images && prod.images[0]));
          if (!img) return;
          fill.style.backgroundImage = 'url(' + img + ')';
          el.classList.add('is-loaded');
        })
        .catch(function () {});
    });
  }

  // ── Interaction ─────────────────────────────────────────────────────
  function updateTotals(model) {
    var monthly = 0, once = 0, count = 0;
    model.stacks.forEach(function (m) {
      var card = panels.plan.querySelector('[data-ytp-stack="' + CSS.escape(m.id) + '"]');
      var line = card && card.querySelector('[data-ytp-line]');
      var price = priceFor(m.raw, m.mode);
      if (line) line.textContent = m.include ? money(price) + (m.mode === 'onetime' ? '' : ' a month') : 'Not included';
      if (!m.include) return;
      count += 1;
      if (m.mode === 'onetime') once += price; else monthly += price;
    });
    var totalEl = panels.plan.querySelector('[data-ytp-total]');
    var detailEl = panels.plan.querySelector('[data-ytp-total-detail]');
    var btn = panels.plan.querySelector('[data-ytp-add]');
    var text, detail;
    if (!count) { text = '—'; detail = 'No blends selected'; }
    else if (monthly && once) { text = money(monthly) + ' a month'; detail = '+ ' + money(once) + ' one-off · ' + count + (count === 1 ? ' blend' : ' blends'); }
    else if (monthly) { text = money(monthly) + ' a month'; detail = count + (count === 1 ? ' blend' : ' blends') + ' · ' + money(round2(monthly / 30)) + ' a day'; }
    else { text = money(once); detail = count + (count === 1 ? ' blend' : ' blends') + ' · one-off'; }
    if (totalEl) totalEl.textContent = text;
    if (detailEl) detailEl.textContent = detail;
    if (btn) btn.disabled = !count || model.busy;
  }

  function showError(msg) {
    var el = panels.plan.querySelector('[data-ytp-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = !msg;
    if (!msg) return;
    var p = document.createElement('p');
    p.textContent = msg;
    el.appendChild(p);
  }
  function showRetryInline(model, msg) {
    showError(msg);
    var el = panels.plan.querySelector('[data-ytp-error]');
    if (!el) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ytp-btn ytp-btn--ghost ytp-btn--small';
    b.textContent = 'Try again';
    b.addEventListener('click', function () { addToBasket(model); });
    el.appendChild(b);
  }
  function consentError(show) {
    var err = panels.plan.querySelector('[data-ytp-consent-err]');
    var block = panels.plan.querySelector('[data-ytp-consent-block]');
    if (err) err.hidden = !show;
    if (block) block.classList.toggle('is-invalid', !!show);
    if (show) {
      var cb = panels.plan.querySelector('[data-ytp-consent]');
      if (cb) { try { cb.focus({ preventScroll: false }); } catch (_e) {} }
    }
  }

  function bind(model) {
    model.stacks.forEach(function (m) {
      var card = panels.plan.querySelector('[data-ytp-stack="' + CSS.escape(m.id) + '"]');
      if (!card) return;
      var inc = card.querySelector('[data-ytp-include]');
      if (inc) inc.addEventListener('change', function () {
        m.include = inc.checked;
        card.classList.toggle('is-off', !m.include);
        card.querySelectorAll('[data-ytp-mode]').forEach(function (r) { r.disabled = !m.include; });
        selectionChanged(model);
      });
      card.querySelectorAll('[data-ytp-mode]').forEach(function (r) {
        r.addEventListener('change', function () {
          if (!r.checked) return;
          m.mode = r.value;
          card.querySelectorAll('.ytp-plan').forEach(function (l) { l.classList.toggle('is-on', l.contains(r)); });
          selectionChanged(model);
        });
      });
    });
    var cb = panels.plan.querySelector('[data-ytp-consent]');
    if (cb) cb.addEventListener('change', function () { if (cb.checked) consentError(false); consentNote(model); });
    var btn = panels.plan.querySelector('[data-ytp-add]');
    if (btn) btn.addEventListener('click', function () { addToBasket(model); });
    consentNote(model);
  }

  /**
   * The line under the button says, while sharing is not yet agreed, why
   * checkout will not open (UAT 9 Oct 2026: the box is required, and the
   * page says so before anyone presses the button, not only after).
   */
  function consentNote(model) {
    var note = panels.plan.querySelector('[data-ytp-note]');
    if (!note) return;
    var cb = panels.plan.querySelector('[data-ytp-consent]');
    var agreed = model.granted || !!(cb && cb.checked);
    note.textContent = agreed
      ? 'You can review everything at checkout before you pay.'
      : 'Checkout opens once you have ticked the sharing box above — it is required to use this plan.';
    note.classList.toggle('ytp-summary__note--req', !agreed);
  }

  // ── Add to basket ───────────────────────────────────────────────────
  function buttonLabel(model) {
    return model.busy ? 'Taking you to checkout…' : model.added ? 'Go to checkout' : cfg.addLabel;
  }
  // Any change to the selection after a successful add turns the button
  // back into "Add to basket" (a second click without changes would only
  // re-add the same lines).
  function selectionChanged(model) {
    model.added = false;
    var btn = panels.plan.querySelector('[data-ytp-add]');
    if (btn) btn.textContent = buttonLabel(model);
    updateTotals(model);
    showError('');
  }
  function setBusy(model, on) {
    model.busy = on;
    var btn = panels.plan.querySelector('[data-ytp-add]');
    if (btn) { btn.disabled = on; btn.textContent = buttonLabel(model); btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    panels.plan.querySelectorAll('[data-ytp-include], [data-ytp-mode], [data-ytp-consent]').forEach(function (i) {
      if (on) i.setAttribute('data-ytp-was-disabled', i.disabled ? '1' : '');
      i.disabled = on ? true : i.getAttribute('data-ytp-was-disabled') === '1';
    });
    if (!on) updateTotals(model);
  }

  /**
   * Checkout, through the discount when the server gave us one. This is
   * the ONLY place this file sends anyone to checkout.
   */
  function goToCheckout() {
    leaveFor(pendingDiscountUrl || '/checkout');
  }

  /**
   * After the lines are in: straight to checkout (UAT 23 Sep 2026). The
   * side cart is deliberately not opened from this page any more; the
   * drawer intercept stays for someone who opens the basket themselves.
   */
  function goToBasket() {
    goToCheckout();
    return Promise.resolve();
  }

  /**
   * Whatever the drawer (or the page) offers as "checkout", take it through
   * /discount/<CODE> first. Capture phase, so we get there before the
   * theme's own handler; and only ever when the server has handed us a URL,
   * so a plan with no discount — and every ordinary page — is untouched.
   */
  function interceptCheckout() {
    var SEL = 'a[href^="/checkout"], [name="checkout"], form[action^="/checkout"] button';
    document.addEventListener('click', function (ev) {
      if (!pendingDiscountUrl || leaving || ev.defaultPrevented) return;
      if (ev.button > 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      var el = ev.target && ev.target.closest && ev.target.closest(SEL);
      if (!el) return;
      ev.preventDefault();
      ev.stopPropagation();
      goToCheckout();
    }, true);
    document.addEventListener('submit', function (ev) {
      if (!pendingDiscountUrl || leaving || ev.defaultPrevented) return;
      var form = ev.target;
      if (!form || !form.getAttribute) return;
      if (String(form.getAttribute('action') || '').indexOf('/checkout') !== 0) return;
      ev.preventDefault();
      ev.stopPropagation();
      goToCheckout();
    }, true);
  }

  async function addToBasket(model) {
    if (model.busy) return;
    if (model.added) { await goToBasket(); return; }
    showError('');
    var selections = model.stacks.filter(function (m) { return m.include; })
      .map(function (m) { return { stack_id: m.id, mode: m.mode }; });
    if (!selections.length) { showError('Include at least one blend to continue.'); return; }
    var cb = panels.plan.querySelector('[data-ytp-consent]');
    if (!model.granted && !(cb && cb.checked)) { consentError(true); return; }
    consentError(false);
    setBusy(model, true);

    // 1. Server builds the cart lines (and records consent).
    var res, body;
    try {
      res = await fetch(PROXY + '/partner-plan/' + encodeURIComponent(model.token) + '/cart', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ selections: selections, consent: { accepted: true, version: model.version } }),
      });
    } catch (_e) {
      setBusy(model, false);
      return showRetryInline(model, 'We couldn’t reach Yutrients. Check your connection and try again.');
    }
    body = await safeJson(res);
    if (res.status === 404) { setBusy(model, false); return setState('invalid'); }
    if (res.status === 410) { setBusy(model, false); return setState(stateFor(body && body.state)); }
    if (res.status === 422) {
      setBusy(model, false);
      var code = body && (body.error || body.code || body.reason);
      if (code === 'consent_required') {
        if (model.granted) {
          // The server no longer holds a current consent — ask again.
          model.granted = false;
          var block = panels.plan.querySelector('[data-ytp-consent-block]');
          if (block) {
            var plan = { consent: { version: model.version, text: '' } };
            block.outerHTML = consentBlockHtml(plan, model.pracName, model.pracFirst, cfg.privacyUrl, cfg.sharingUrl);
            var cb2 = panels.plan.querySelector('[data-ytp-consent]');
            if (cb2) cb2.addEventListener('change', function () { if (cb2.checked) consentError(false); consentNote(model); });
          }
          consentNote(model);
        }
        if (body && body.version) model.version = body.version;
        consentError(true);
        return;
      }
      // A 90-day plan that isn't available: drop it for that blend (or all
      // blends if the server didn't say which) and ask for another choice.
      var sid = body && (body.stack_id || (body.details && body.details.stack_id));
      var dropped = false;
      model.stacks.forEach(function (m) {
        if (m.mode !== '90d') return;
        if (sid && String(sid) !== String(m.id)) return;
        m.plans = m.plans.filter(function (p) { return p !== '90d'; });
        m.mode = m.plans.length ? defaultMode(m.raw, m.plans) : 'sub';
        var card = panels.plan.querySelector('[data-ytp-stack="' + CSS.escape(m.id) + '"]');
        var fs = card && card.querySelector('.ytp-plans');
        if (fs) {
          fs.innerHTML = '<legend class="ytp-eyebrow">How would you like it?</legend>' + planOptionsHtml(m, model.pracFirst);
          fs.querySelectorAll('[data-ytp-mode]').forEach(function (r) {
            r.addEventListener('change', function () {
              if (!r.checked) return;
              m.mode = r.value;
              card.querySelectorAll('.ytp-plan').forEach(function (l) { l.classList.toggle('is-on', l.contains(r)); });
              selectionChanged(model);
            });
          });
        }
        dropped = true;
      });
      updateTotals(model);
      return showError(dropped
        ? 'That option isn’t available for that blend right now, so we’ve switched it. Check the choice and try again.'
        : (body && body.message) || 'Something in the plan couldn’t be added. Check your choices and try again.');
    }
    if (!res.ok || !body || !Array.isArray(body.lines) || !body.lines.length) {
      setBusy(model, false);
      return showRetryInline(model, 'Something went wrong preparing your basket. Please try again.');
    }

    // 1b. Where checkout goes from here (founder 17 Sep 2026). Re-read on
    //     every add, so a plan that stops carrying a discount stops
    //     redirecting; anything we don't recognise is simply no discount.
    pendingDiscountUrl = safeDiscountUrl(body.discount_url);

    // 2. ONE /cart/add.js call, lines reversed (Shopify prepends).
    var items = body.lines.map(function (l) {
      var it = { id: Number(l.id), quantity: Number(l.quantity) || 1, properties: l.properties || {} };
      if (l.selling_plan != null && l.selling_plan !== '') it.selling_plan = Number(l.selling_plan);
      return it;
    });
    var addRes;
    try {
      addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items.slice().reverse() }),
      });
    } catch (_e) {
      setBusy(model, false);
      return showRetryInline(model, 'We couldn’t reach the basket. Check your connection and try again.');
    }
    if (!addRes.ok) {
      var detail = 'Something went wrong adding your plan to the basket. Please try again.';
      var err = await safeJson(addRes);
      if (err && (err.description || err.message)) detail = err.description || err.message;
      setBusy(model, false);
      return showRetryInline(model, detail);
    }

    // 3. Mark the cart as a practitioner-plan cart so yt-referral.js leaves
    //    its attributes alone. No send id is exposed by the contract, so the
    //    first group's key identifies the plan; a token hash is the fallback.
    var planRef = (body.send_id || (body.groups && body.groups[0] && body.groups[0].group_key)) || null;
    if (!planRef) planRef = await tokenHashPrefix(model.token);
    var attrs = {};
    attrs[PLAN_ATTR] = String(planRef);
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (_e) { /* the lines are in; the attribute is best-effort */ }

    // 4. Straight to checkout. The token stays in sessionStorage
    //    (tab-scoped) so coming back still shows the plan; the button reads
    //    "Go to checkout" until a choice changes.
    model.added = true;
    setBusy(model, false);
    await goToBasket();
  }

  // ── Init ────────────────────────────────────────────────────────────
  function boot() {
    root = document.querySelector('[data-yt-practitioner-plan]');
    if (!root) return;
    panels = {};
    root.querySelectorAll('[data-ytp-state]').forEach(function (el) { panels[el.getAttribute('data-ytp-state')] = el; });
    cfg = {
      addLabel: root.getAttribute('data-ytp-add-label') || 'Checkout',
      privacyUrl: root.getAttribute('data-ytp-privacy-url') || '/pages/privacy-policy',
      sharingUrl: root.getAttribute('data-ytp-sharing-url') || '/pages/manage-subscription',
    };
    interceptCheckout();
    var token = readToken();
    if (!token) return setState('invalid');
    loadPlan(token);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
